/**
 * 워크스페이스 `brandSetting.basicFilter.persona` ↔ 사이드바 페르소나 상태
 * (`adsEval` boolean[], `isOfficialAccount` boolean[]) 매핑.
 * 저장 토큰은 UI 문구 변경에 영향 없도록 영문 상수를 사용한다.
 */

import type { MomentPersonaSelection } from 'src/types/moments/moment-scope';

const BRAND_PERSONA_ORGANIC = 'organic';
const BRAND_PERSONA_SPONSORED = 'sponsored';
const BRAND_PERSONA_BRAND_ACCOUNT = 'brandAccount';

/** 저장 토큰 셋 — 차례가 곧 화면의 차례다(일반 · 광고/협찬 · 브랜드 계정) */
export const BRAND_PERSONA_TOKENS = [BRAND_PERSONA_ORGANIC, BRAND_PERSONA_SPONSORED, BRAND_PERSONA_BRAND_ACCOUNT] as const;
export type BrandPersonaToken = (typeof BRAND_PERSONA_TOKENS)[number];

const ALL_PERSONA_TOKENS: readonly string[] = BRAND_PERSONA_TOKENS;

const TOKEN_ALIAS_TO_CANONICAL: Record<string, string> = {
  [BRAND_PERSONA_ORGANIC]: BRAND_PERSONA_ORGANIC,
  일반: BRAND_PERSONA_ORGANIC,
  general: BRAND_PERSONA_ORGANIC,

  [BRAND_PERSONA_SPONSORED]: BRAND_PERSONA_SPONSORED,
  '광고/협찬': BRAND_PERSONA_SPONSORED,
  광고: BRAND_PERSONA_SPONSORED,

  [BRAND_PERSONA_BRAND_ACCOUNT]: BRAND_PERSONA_BRAND_ACCOUNT,
  브랜드계정: BRAND_PERSONA_BRAND_ACCOUNT,
  '브랜드 계정': BRAND_PERSONA_BRAND_ACCOUNT,
  brandaccount: BRAND_PERSONA_BRAND_ACCOUNT,
};

function toBoolOrNull(v: unknown): boolean | null {
  if (v === true || v === false) return v;
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'true' || s === '1') return true;
  if (s === 'false' || s === '0') return false;
  return null;
}

/**
 * @param {unknown} v
 * @returns {string|null} canonical token or null
 */
function mapPersonaLabelToCanonicalToken(v: unknown): string | null {
  const raw = String(v ?? '').trim();
  if (!raw) return null;
  if (Object.prototype.hasOwnProperty.call(TOKEN_ALIAS_TO_CANONICAL, raw)) {
    return TOKEN_ALIAS_TO_CANONICAL[raw];
  }
  const lower = raw.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(TOKEN_ALIAS_TO_CANONICAL, lower)) {
    return TOKEN_ALIAS_TO_CANONICAL[lower];
  }
  return null;
}

/**
 * 저장용 persona 배열: 허용 토큰만, 중복 제거, 정렬.
 * 세 가지가 모두 포함되면 UI "전체"와 동일하게 빈 배열로 정규화한다.
 * @param {unknown[]} raw
 * @returns {string[]}
 */
export function sanitizePersonaTokensForStorage(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const mapped = raw.map(mapPersonaLabelToCanonicalToken).filter((t): t is string => Boolean(t));
  const uniq = [...new Set(mapped)];
  if (uniq.length === 3 && ALL_PERSONA_TOKENS.every((t) => uniq.includes(t))) return [];
  return uniq.sort((a, b) => ALL_PERSONA_TOKENS.indexOf(a) - ALL_PERSONA_TOKENS.indexOf(b));
}

function normalizeAdsEvalArray(raw: unknown): boolean[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map(toBoolOrNull).filter((b): b is boolean => b === true || b === false))];
}

function normalizeOfficialArray(isOfficialAccount: unknown, isOfficial: unknown): boolean[] {
  const src = Array.isArray(isOfficialAccount)
    ? isOfficialAccount
    : (Array.isArray(isOfficial) ? isOfficial : []);
  const bools = src.map(toBoolOrNull).filter((b): b is boolean => b === true || b === false);
  return bools.includes(true) ? [true] : [];
}

/**
 * 사이드바 `adsEval` / `isOfficialAccount` → 저장용 persona 토큰 배열.
 * @param {{ adsEval?: unknown, isOfficialAccount?: unknown, isOfficial?: unknown }} p
 * @returns {string[]}
 */
export function sidebarArraysToPersonaTokens({
  adsEval,
  isOfficialAccount,
  isOfficial,
}: { adsEval?: unknown; isOfficialAccount?: unknown; isOfficial?: unknown } = {}): string[] {
  const ads = normalizeAdsEvalArray(adsEval);
  const official = normalizeOfficialArray(isOfficialAccount, isOfficial);

  if (ads.length === 0 && official.length === 0) return [];

  const hasOrganic = ads.includes(false);
  const hasSponsored = ads.includes(true);
  const hasBrand = official.includes(true);
  if (hasOrganic && hasSponsored && hasBrand) return [];

  const out: string[] = [];
  if (hasOrganic) out.push(BRAND_PERSONA_ORGANIC);
  if (hasSponsored) out.push(BRAND_PERSONA_SPONSORED);
  if (hasBrand) out.push(BRAND_PERSONA_BRAND_ACCOUNT);
  return out;
}

/**
 * 저장된 persona 토큰 배열 → 사이드바용 `adsEval`, `isOfficialAccount`.
 * @param {unknown[]} tokens
 * @returns {{ adsEval: boolean[], isOfficialAccount: boolean[] }}
 */
export function personaTokensToSidebarPersonaArrays(tokens: unknown): { adsEval: boolean[]; isOfficialAccount: boolean[] } {
  const canonical = sanitizePersonaTokensForStorage(tokens);
  if (canonical.length === 0) {
    return { adsEval: [], isOfficialAccount: [] };
  }

  const adsEval: boolean[] = [];
  if (canonical.includes(BRAND_PERSONA_ORGANIC)) adsEval.push(false);
  if (canonical.includes(BRAND_PERSONA_SPONSORED)) adsEval.push(true);
  const isOfficialAccount = canonical.includes(BRAND_PERSONA_BRAND_ACCOUNT) ? [true] : [];
  return { adsEval, isOfficialAccount };
}

/**
 * 레거시 `adsEval` / `isOfficial` / `isOfficialAccount`(문자열·boolean 혼재) 정규화.
 * @param {Object} savedFilter
 * @returns {{ adsEval: boolean[], isOfficialAccount: boolean[] }}
 */
function personaFromLegacyAdsOfficial(savedFilter: any): { adsEval: boolean[]; isOfficialAccount: boolean[] } {
  const rawAds: unknown[] = Array.isArray(savedFilter?.adsEval) ? savedFilter.adsEval : [];
  const adsEval = [...new Set(rawAds.map(toBoolOrNull).filter((b): b is boolean => b === true || b === false))];

  const rawOfficial: unknown[] = Array.isArray(savedFilter?.isOfficialAccount)
    ? savedFilter.isOfficialAccount
    : (Array.isArray(savedFilter?.isOfficial) ? savedFilter.isOfficial : []);
  const officialBools = rawOfficial.map(toBoolOrNull).filter((b): b is boolean => b === true || b === false);
  const isOfficialAccount: boolean[] = officialBools.includes(true) ? [true] : [];

  return { adsEval, isOfficialAccount };
}

/**
 * 워크스페이스 basicFilter에서 페르소나 사이드바 배열 복원.
 * `persona` 필드가 있으면 우선, 없으면 레거시 adsEval/isOfficial 계열.
 * @param {Object|null|undefined} savedFilter
 * @returns {{ adsEval: boolean[], isOfficialAccount: boolean[] }}
 */
export function personaFromSavedBasicFilter(savedFilter: any): { adsEval: boolean[]; isOfficialAccount: boolean[] } {
  if (!savedFilter || typeof savedFilter !== 'object') {
    return { adsEval: [], isOfficialAccount: [] };
  }
  if (Object.prototype.hasOwnProperty.call(savedFilter, 'persona') && Array.isArray(savedFilter.persona)) {
    return personaTokensToSidebarPersonaArrays(savedFilter.persona);
  }
  return personaFromLegacyAdsOfficial(savedFilter);
}

/**
 * 아는 토큰만 고른다 — 별칭(화면 문구)은 받지 않는다 · 중복 제거 · 토큰 차례로.
 * ⚠️ 셋 다 골라도 셋 그대로 둔다(고른 것을 되살리는 자리) — 「조건 없음」 판정은 `personaTokensToSelection` 이 한다.
 * @param raw 세션·주소에서 온 값 — 배열이 아니면 빈 목록
 */
export function pickPersonaTokens(raw: unknown): BrandPersonaToken[] {
  if (!Array.isArray(raw)) return [];
  return BRAND_PERSONA_TOKENS.filter((token) => raw.includes(token));
}

/**
 * 저장 토큰 → 질의의 페르소나 조건(`MomentPersonaSelection`).
 * @returns 비었거나 셋 다면 `undefined` — 조건 없음이라 요청에 싣지 않는다
 */
export function personaTokensToSelection(tokens: readonly unknown[]): MomentPersonaSelection | undefined {
  const { adsEval, isOfficialAccount } = personaTokensToSidebarPersonaArrays([...tokens]);
  if (adsEval.length === 0 && isOfficialAccount.length === 0) return undefined;
  return { adsEval, officialOnly: isOfficialAccount.includes(true) };
}
